/**
 * PII fields that must NEVER be sent to GA4.
 * Sending PII violates Google Analytics ToS and can cause property suspension.
 * @type {Set<string>}
 */
const PII_FIELDS = new Set([
    'email',
    'contact_name',
    'contact_phone',
    'profile_picture',
    'user_id',
    'uuid',
    'password',
    'referred_by',
]);

/**
 * Strips PII fields from event parameters before sending to GA4.
 * @param {Object} params - The raw event parameters
 * @returns {Object} Sanitized parameters without PII
 */
function sanitizeParams(params) {
    const sanitized = {};
    for (const [key, value] of Object.entries(params)) {
        if (!PII_FIELDS.has(key)) {
            sanitized[key] = value;
        }
    }
    return sanitized;
}

export default {
    isDebugMode: process.env.VUE_APP_ENVIROMENT === 'local',

    debugLog(message, data = null) {
        if (this.isDebugMode) {
            console.log(`[Analytics Debug] ${message}`, data || '');
        }
    },

    /**
     * Tracks a custom event in GA4 and Meta Pixel.
     * PII fields are automatically stripped before sending.
     * @param {string} eventName - The event name (must be snake_case, max 40 chars)
     * @param {Object} [parameters={}] - Event parameters (PII will be filtered out)
     */
    trackEvent(eventName, parameters = {}) {
        try {
            const enhancedParameters = {
                ...sanitizeParams(parameters),
                environment: process.env.VUE_APP_ENVIROMENT,
                timestamp: new Date().toISOString(),
            };

            // Send to GA4 via gtag
            if (typeof gtag !== 'undefined' && window.GA_INITIALIZED) {
                this.debugLog('Tracking via gtag:', { eventName, enhancedParameters });
                gtag('event', eventName, enhancedParameters);
            }

            fbq('trackCustom', eventName, { status: 'succeed' });
        } catch (error) {
            console.error('Error tracking event:', error);
        }
    },

    /**
     * Tracks an error event in GA4.
     * @param {Error} error - The error object
     * @param {Object} [additionalInfo={}] - Additional context
     */
    trackError(error, additionalInfo = {}) {
        this.trackEvent('error_occurred', {
            error_message: error.message,
            error_stack: error.stack,
            error_type: error.name,
            ...additionalInfo,
        });
    },

    /**
     * Sets user properties in GA4 (non-PII only).
     * @param {string} userId - Opaque user/account ID (not email)
     * @param {Object} [userProperties={}] - Custom user properties
     */
    trackUser(userId, userProperties = {}) {
        if (typeof gtag !== 'undefined') {
            gtag(
                'set',
                'user_properties',
                sanitizeParams({
                    user_id: userId,
                    ...userProperties,
                }),
            );
        }
    },
};
